import { getDefaultFixtureForComponent } from '@web-studio/core-fixtures';
import { enumerateBlockThemeMatrix } from '@web-studio/layout-matrix';
import type { MatrixCell } from '@web-studio/layout-matrix';
import { Link, useSearchParams } from 'react-router-dom';
import { MatrixCanvas, RegistryOnlyPlaceholder } from '@/components/MatrixCanvas';
import {
  matrixStateToSearchParams,
  parseMatrixSearchParams,
  resolveFixtureBlocks,
} from '@/lib/matrixState';
import type { LayoutComponentType } from '@web-studio/shared-schemas';
import { listImplementedComponentTypes } from '@web-studio/layout-matrix';

export function GalleryPage() {
  const [searchParams] = useSearchParams();
  const showRegistryOnly = searchParams.get('registry') !== '0';
  const filterType = searchParams.get('block') ?? '';

  const cells = enumerateBlockThemeMatrix({
    includeRegistryOnly: showRegistryOnly,
  }).filter((c) => {
    if (!filterType) return c.implementationStatus === 'rendered';
    return c.componentType === filterType;
  });

  const renderedSample = cells
    .filter((c) => c.implementationStatus === 'rendered')
    .slice(0, 48);

  const componentTypes = listImplementedComponentTypes();

  return (
    <div className="lab-page">
      <header className="lab-page-header">
        <h1 className="lab-page-header__title">Gallery</h1>
        <p className="lab-page-header__desc">
          Thumbnail matrix · {renderedSample.length} rendered cells shown (cap 48) ·{' '}
          {cells.length} total matching filter
        </p>
      </header>

      <div className="lab-toolbar">
        <div className="lab-field">
          <label htmlFor="gallery-block">Filter block</label>
          <select
            id="gallery-block"
            value={filterType}
            onChange={(e) => {
              const p = new URLSearchParams(searchParams);
              if (e.target.value) p.set('block', e.target.value);
              else p.delete('block');
              window.location.search = p.toString();
            }}
          >
            <option value="">All implemented</option>
            {componentTypes.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--lab-muted)' }}>
          <input
            type="checkbox"
            checked={showRegistryOnly}
            onChange={(e) => {
              const p = new URLSearchParams(searchParams);
              if (e.target.checked) p.delete('registry');
              else p.set('registry', '0');
              window.location.search = p.toString();
            }}
          />
          Include registry-only placeholders
        </label>
      </div>

      <div className="lab-grid">
        {renderedSample.map((cell) => (
          <GalleryCard key={cell.cellId} cell={cell} />
        ))}
      </div>
    </div>
  );
}

function GalleryCard({ cell }: { cell: MatrixCell }) {
  const fixture = getDefaultFixtureForComponent(cell.componentType as LayoutComponentType);
  const blocks =
    cell.implementationStatus === 'rendered' && fixture
      ? [fixture.block]
      : resolveFixtureBlocks(cell.fixtureId);

  const isolateState = parseMatrixSearchParams(
    new URLSearchParams({
      fixture: cell.fixtureId,
      theme: cell.themeId,
      colorset: cell.colorsetId,
      spacing: cell.spacingEnum,
      locale: cell.locale,
    }),
    cell.componentType as LayoutComponentType,
  );
  const isolateParams = matrixStateToSearchParams(isolateState);

  return (
    <Link
      to={`/matrix/block/${cell.componentType}?${isolateParams.toString()}`}
      className="lab-grid-card block no-underline"
      style={{ color: 'inherit' }}
    >
      <div className="lab-grid-card__preview">
        {cell.implementationStatus === 'rendered' && blocks.length > 0 ? (
          <MatrixCanvas
            blocks={blocks}
            locale={cell.locale}
            themeId={cell.themeId}
            colorsetId={cell.colorsetId}
            spacingEnum={cell.spacingEnum}
            viewport="mobile"
            compact
          />
        ) : (
          <RegistryOnlyPlaceholder
            registryKey={String(cell.componentType)}
            themeId={cell.themeId}
            colorsetId={cell.colorsetId}
          />
        )}
      </div>
      <div className="p-2 text-xs">
        <p className="font-medium">{cell.componentType}</p>
        <p style={{ color: 'var(--lab-muted)' }}>
          {cell.themeId} · {cell.colorsetId} · {cell.spacingEnum} · {cell.locale}
        </p>
        <span
          className={`lab-badge ${cell.implementationStatus === 'rendered' ? 'lab-badge--ok' : 'lab-badge--warn'}`}
        >
          {cell.implementationStatus}
        </span>
      </div>
    </Link>
  );
}
